import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, Mail, Plus, UserCog } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import type { Partner } from '@/types'
import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import Modal from '@/components/ui/Modal'
import PageHeader from '@/components/ui/PageHeader'
import SetPasswordModal from '@/components/admin/SetPasswordModal'
import { Field, Input } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import { Table, Row, Cell, EmptyRow, actionsColumn, actionsCellClass } from '@/components/ui/Table'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'

interface Draft {
  id?: number
  name: string
  code: string
  contact_person: string
  phone: string
  email: string
  status: 'approved' | 'suspended'
}

const emptyDraft: Draft = { name: '', code: '', contact_person: '', phone: '', email: '', status: 'approved' }

function toDraft(partner: Partner): Draft {
  return {
    id: partner.id,
    name: partner.name,
    code: partner.code ?? '',
    contact_person: partner.contact_person ?? '',
    phone: partner.phone ?? '',
    email: partner.email ?? '',
    status: partner.status === 'suspended' ? 'suspended' : 'approved',
  }
}

/**
 * External warehouse partners: the company and its single login, managed on
 * one screen. Partners are not team members, so they never appear under
 * Staff & Admins.
 */
export default function AdminPartners() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [confirmInvite, setConfirmInvite] = useState<Partner | null>(null)
  const [passwordFor, setPasswordFor] = useState<Partner | null>(null)

  const partners = useQuery({
    queryKey: ['admin-partners'],
    queryFn: async () => (await api.get<{ data: Partner[] }>('/admin/partners')).data.data,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-partners'] })

  const save = useMutation({
    mutationFn: async (d: Draft) => {
      const payload = {
        name: d.name,
        code: d.code || null,
        contact_person: d.contact_person || null,
        phone: d.phone || null,
        email: d.email,
        status: d.status,
      }
      return d.id
        ? (await api.patch(`/admin/partners/${d.id}`, payload)).data
        : (await api.post('/admin/partners', payload)).data
    },
    onSuccess: (res: { message?: string }) => {
      invalidate()
      setDraft(null)
      toast.success(res.message ?? 'Saved')
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  const invite = useMutation({
    mutationFn: async (id: number) => (await api.post(`/admin/partners/${id}/invite`)).data,
    onSuccess: (res: { message?: string }) => {
      setConfirmInvite(null)
      toast.success('Invite sent', res.message)
    },
    onError: (err) => {
      setConfirmInvite(null)
      toast.error('Could not send the invite', errorMessage(err))
    },
  })

  const setStatus = useMutation({
    mutationFn: async ({ partner, status }: { partner: Partner; status: Draft['status'] }) =>
      (await api.patch(`/admin/partners/${partner.id}`, { ...toDraft(partner), code: partner.code ?? null, status })).data,
    onSuccess: (res: { message?: string }) => {
      invalidate()
      toast.success(res.message ?? 'Updated')
    },
    onError: (err) => toast.error('Could not update', errorMessage(err)),
  })

  if (partners.isLoading) return <LogoLoader />

  // The partner behind the open edit modal, for the Set password shortcut.
  const editing = draft?.id ? partners.data?.find((partner) => partner.id === draft.id) : undefined

  return (
    <div>
      <PageHeader
        title="External warehouse partners"
        subtitle="Companies you can hand a whole job to. Each has one login that updates the job's steps from their side."
        actions={
          <Button onClick={() => setDraft(emptyDraft)}>
            <Plus className="size-4" /> Add partner
          </Button>
        }
      />

      <Table columns={['Partner', 'Code', 'Contact', 'Phone', 'Login email', 'Status', actionsColumn]}>
        {(partners.data?.length ?? 0) === 0 && <EmptyRow colSpan={7}>No partners yet.</EmptyRow>}
        {partners.data?.map((partner) => (
          <Row key={partner.id} onClick={() => setDraft(toDraft(partner))}>
            <Cell className="font-semibold">{partner.name}</Cell>
            <Cell className="font-mono text-xs font-bold">
              {partner.code ?? <span className="font-sans font-normal text-muted">not set</span>}
            </Cell>
            <Cell>{partner.contact_person || '-'}</Cell>
            <Cell>{partner.phone || '-'}</Cell>
            <Cell>{partner.email ?? <span className="text-muted">no login yet</span>}</Cell>
            <Cell>
              <Badge
                tone={partner.status === 'approved' ? 'success' : partner.status === 'suspended' ? 'danger' : 'warning'}
              >
                {partner.status === 'approved' ? 'active' : partner.status === 'suspended' ? 'suspended' : 'no login'}
              </Badge>
            </Cell>
            <Cell className={actionsCellClass}>
              <div className="flex justify-end gap-1.5">
                {partner.email && (
                  <Button
                    variant="ghost"
                    className="px-2"
                    onClick={(e) => {
                      e.stopPropagation()
                      setConfirmInvite(partner)
                    }}
                    title="Email a link to set their password"
                    aria-label={`Email ${partner.name} a link to set their password`}
                  >
                    <Mail className="size-4" />
                  </Button>
                )}
                {partner.account_id && (
                  <Button
                    variant="ghost"
                    className="px-2"
                    onClick={(e) => {
                      e.stopPropagation()
                      setPasswordFor(partner)
                    }}
                    title="Set their password"
                    aria-label={`Set a password for ${partner.name}`}
                  >
                    <KeyRound className="size-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  className="px-2"
                  onClick={(e) => {
                    e.stopPropagation()
                    setDraft(toDraft(partner))
                  }}
                >
                  <UserCog className="size-4" />
                </Button>
                {partner.status === 'approved' && (
                  <Button
                    variant="ghost"
                    className="px-2 text-danger"
                    loading={setStatus.isPending && setStatus.variables?.partner.id === partner.id}
                    onClick={(e) => {
                      e.stopPropagation()
                      setStatus.mutate({ partner, status: 'suspended' })
                    }}
                  >
                    Suspend
                  </Button>
                )}
                {partner.status === 'suspended' && (
                  <Button
                    variant="ghost"
                    className="px-2"
                    loading={setStatus.isPending && setStatus.variables?.partner.id === partner.id}
                    onClick={(e) => {
                      e.stopPropagation()
                      setStatus.mutate({ partner, status: 'approved' })
                    }}
                  >
                    Reactivate
                  </Button>
                )}
              </div>
            </Cell>
          </Row>
        ))}
      </Table>

      <Modal
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?.id ? 'Edit partner' : 'Add partner'}
        footer={
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            {editing?.account_id && (
              <Button
                variant="outline"
                className="mr-auto"
                onClick={() => {
                  setDraft(null)
                  setPasswordFor(editing)
                }}
              >
                <KeyRound className="size-4" /> Set password
              </Button>
            )}
            <Button variant="outline" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button loading={save.isPending} disabled={!draft?.name || !draft?.code || !draft?.email} onClick={() => draft && save.mutate(draft)}>
              Save
            </Button>
          </div>
        }
      >
        {draft && (
          <div className="space-y-4">
            <Field label="Company name" htmlFor="name">
              <Input id="name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </Field>
            <Field label="Partner code" htmlFor="code" required>
              <Input
                id="code"
                className="font-mono uppercase"
                maxLength={5}
                autoCapitalize="characters"
                value={draft.code}
                onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
              />
              <p className="text-xs text-foreground/60">
                Shown in place of MPG on every job given to this partner, e.g.{' '}
                <span className="font-mono font-bold">{draft.code || 'AI'}-2026-00006</span>. 2 to 5 letters or digits.
              </p>
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Contact person" htmlFor="contact_person">
                <Input
                  id="contact_person"
                  value={draft.contact_person}
                  onChange={(e) => setDraft({ ...draft, contact_person: e.target.value })}
                />
              </Field>
              <Field label="Phone" htmlFor="phone">
                <Input id="phone" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
              </Field>
            </div>
            <Field label="Login email" htmlFor="email">
              <Input
                id="email"
                type="email"
                value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              />
            </Field>
            {draft.id && (
              <Field label="Login status" htmlFor="status">
                <Select
                  id="status"
                  value={draft.status}
                  onChange={(v) => setDraft({ ...draft, status: v as Draft['status'] })}
                  options={[
                    { value: 'approved', label: 'Active' },
                    { value: 'suspended', label: 'Suspended' },
                  ]}
                />
              </Field>
            )}
            {!editing?.account_id && (
              <p className="text-xs text-foreground/60">
                The partner receives an email with a link to choose their own password. You can also set one for
                them afterwards with the key button on their row.
              </p>
            )}
          </div>
        )}
      </Modal>

      <SetPasswordModal
        account={
          passwordFor?.email
            ? { name: passwordFor.name, email: passwordFor.email, endpoint: `/admin/partners/${passwordFor.id}/password` }
            : null
        }
        onClose={() => setPasswordFor(null)}
      />

      <ConfirmDialog
        open={!!confirmInvite}
        onClose={() => setConfirmInvite(null)}
        onConfirm={() => confirmInvite && invite.mutate(confirmInvite.id)}
        title={confirmInvite ? `Email ${confirmInvite.name} a password link?` : ''}
        confirmLabel="Send email"
        loading={invite.isPending}
      >
        A link to set their password goes to {confirmInvite?.email}. It works for 60 minutes. Their current
        password keeps working until they use it.
      </ConfirmDialog>
    </div>
  )
}
